import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Zap, Loader2, CheckCircle2, Shield, Smartphone, Mail, MessageSquare, Download, Copy, Check } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function Setup2FAPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("totp");
  const [copied, setCopied] = useState(false);
  
  // 2FA Setup Data
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  
  // Verification
  const [verificationCode, setVerificationCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    initiate2FASetup();
  }, []);

  const initiate2FASetup = async () => {
    setSetupLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customer/auth/setup-2fa`, {
        method: "totp" // Start with TOTP by default
      }, { withCredentials: true });

      if (response.data.success) {
        setQrCodeUrl(response.data.qr_code);
        setSecret(response.data.secret);
        setBackupCodes(response.data.backup_codes || []);
      } else {
        setError(response.data.error || "Failed to initialize 2FA setup");
      }
    } catch (err) {
      console.error("2FA setup error:", err);
      if (err.response?.status === 401) {
        setError("Please log in first to set up 2FA");
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(err.response?.data?.error || err.response?.data?.detail || "Failed to initialize 2FA setup");
      }
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerifyTOTP = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customer/auth/verify-2fa-setup`, {
        method: "totp",
        code: verificationCode
      }, { withCredentials: true });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setError(response.data.error || "Invalid verification code");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customer/auth/setup-2fa`, {
        method: "email"
      }, { withCredentials: true });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setError(response.data.error || "Failed to set up email 2FA");
      }
    } catch (err) {
      console.error("Email 2FA setup error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Failed to set up email 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySMS = async (e) => {
    e.preventDefault();
    if (!phoneNumber) {
      setError("Please enter your phone number");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customer/auth/setup-2fa`, {
        method: "sms",
        phone_number: phoneNumber
      }, { withCredentials: true });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setError(response.data.error || "Failed to set up SMS 2FA");
      }
    } catch (err) {
      console.error("SMS 2FA setup error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Failed to set up SMS 2FA");
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadBackupCodes = () => {
    const content = `QuantumFlowSync 2FA Backup Codes\n\nGenerated: ${new Date().toLocaleDateString()}\n\n${backupCodes.join('\n')}\n\nKeep these codes in a safe place. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'quantumflowsync-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (setupLoading) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Setting up 2FA...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="glass-heavy rounded-3xl p-8 border border-white/10 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-heading font-semibold text-white mb-2">
              2FA Enabled!
            </h1>
            <p className="text-slate-400 mb-6">
              Your account is now protected with two-factor authentication. Redirecting to dashboard...
            </p>
            <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Redirecting...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="glass-heavy rounded-3xl p-8 border border-white/10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-12 h-12 rounded-xl gradient-active flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-semibold text-white">QuantumFlowSync</span>
            </div>
            <h1 className="text-3xl font-heading font-semibold text-white mb-2">
              Set Up Two-Factor Authentication
            </h1>
            <p className="text-slate-400">
              Add an extra layer of security to your account
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6 bg-white/5">
              <TabsTrigger value="totp" className="data-[state=active]:bg-white/10">
                <Smartphone className="w-4 h-4 mr-2" />
                Authenticator
              </TabsTrigger>
              <TabsTrigger value="email" className="data-[state=active]:bg-white/10">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="sms" className="data-[state=active]:bg-white/10">
                <MessageSquare className="w-4 h-4 mr-2" />
                SMS
              </TabsTrigger>
            </TabsList>

            {/* TOTP Authenticator */}
            <TabsContent value="totp" className="space-y-6">
              <div className="text-center">
                <p className="text-slate-300 mb-4">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                
                {qrCodeUrl && (
                  <div className="inline-block p-4 bg-white rounded-xl mb-4">
                    <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                  </div>
                )}

                <div className="mb-6">
                  <p className="text-slate-400 text-sm mb-2">Or enter this code manually:</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono text-sm">
                      {secret}
                    </code>
                    <button
                      onClick={copySecret}
                      className="p-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                    </button>
                  </div>
                </div>
              </div>

              <form onSubmit={handleVerifyTOTP} className="space-y-4">
                <div>
                  <Label htmlFor="code" className="text-white">Enter 6-digit code from your app</Label>
                  <Input
                    id="code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="mt-1 bg-white/5 border-white/10 text-white text-center text-2xl tracking-widest placeholder:text-slate-500"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Enable 2FA"
                  )}
                </Button>
              </form>

              {backupCodes.length > 0 && (
                <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-start gap-3 mb-3">
                    <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-blue-400 font-medium mb-1">Backup Codes</h3>
                      <p className="text-blue-300 text-sm">
                        Save these codes in a safe place. You can use them to access your account if you lose your authenticator device.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {backupCodes.slice(0, 4).map((code, index) => (
                      <code key={index} className="px-3 py-2 bg-white/5 border border-white/10 rounded text-white font-mono text-sm text-center">
                        {code}
                      </code>
                    ))}
                  </div>
                  <Button
                    onClick={downloadBackupCodes}
                    variant="outline"
                    className="w-full border-blue-500/20 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All Backup Codes
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Email 2FA */}
            <TabsContent value="email" className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Email-Based 2FA</h3>
                <p className="text-slate-400 text-sm">
                  We'll send a verification code to your email each time you log in.
                </p>
              </div>

              <form onSubmit={handleVerifyEmail} className="space-y-4">
                <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-white text-sm">
                    <strong>Your email:</strong> {/* This should be fetched from user context */}
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    You'll receive a 6-digit code at this email address when logging in.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Enabling...
                    </>
                  ) : (
                    "Enable Email 2FA"
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* SMS 2FA */}
            <TabsContent value="sms" className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">SMS-Based 2FA</h3>
                <p className="text-slate-400 text-sm">
                  We'll send a verification code to your phone via SMS each time you log in.
                </p>
              </div>

              <form onSubmit={handleVerifySMS} className="space-y-4">
                <div>
                  <Label htmlFor="phone" className="text-white">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                    required
                  />
                  <p className="text-slate-400 text-xs mt-1">
                    Include country code (e.g., +1 for US)
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !phoneNumber}
                  className="w-full h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Enabling...
                    </>
                  ) : (
                    "Enable SMS 2FA"
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="text-slate-400 hover:text-white transition-colors text-sm"
            >
              Skip for now →
            </button>
          </div>
        </div>

        <div className="text-center mt-6">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="text-slate-400 hover:text-white transition-colors text-sm"
          >
            ← Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
